import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { useToastContext } from '#pplx/frontend/aether/toasts/context/useToastContext.js';
import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { allowsLocalComputerTask, useAsiAccessDecision, useIsFreeTurnBudgetExhausted, useIsFreeTurnPreview, } from '#pplx/frontend/apps/ask/hooks/computer-access-hooks/useAsiAccessDecision.js';
import { useCanUseComputer } from '#pplx/frontend/apps/ask/hooks/computer-access-hooks/useCanUseComputer.js';
import { useViewerComputerRestriction } from '#pplx/frontend/apps/ask/hooks/computer-access-hooks/useViewerComputerRestriction.js';
import { usePaywallUpsell } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/usePaywallUpsell.js';
import { useRefuseViewerSubmission } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/useRefuseViewerSubmission.js';
import { getCreditUpsellReturnPath } from '#pplx/frontend/apps/ask/permalink-slug/creditUpsellReturn.js';
import { getComputerSubmitGate } from '#pplx/frontend/apps/ask/queries/billing-queries/credits/getComputerSubmitGate.js';
import { useAsiUbbConfigForConsumer } from '#pplx/frontend/apps/ask/queries/billing-queries/ubb-config/useAsiUbbConfig.js';
import { getComputerSubmitGateQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/billingKeys.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
// Single-file events: defined here at their only call site rather than in a shared package.
const computerLandingPageUpgradeClickedEvent = EVENT_PAYLOAD;
const computerQuerySubmitAttemptedEvent = {
    event: 'computer query submit attempted',
    data: {},
};
const ComputerAddCreditsUpsellModal = Dynamic(async () => {
    const { ComputerAddCreditsUpsellModal } = await import('#pplx/frontend/apps/ask/hooks/computer-before-submit/ComputerAddCreditsUpsellModal.js');
    return { default: ComputerAddCreditsUpsellModal };
}, {});
const ComputerUpgradeModalChooser = Dynamic(async () => {
    const { ComputerUpgradeModalChooser } = await import('#pplx/frontend/apps/ask/hooks/computer-before-submit/ComputerUpgradeModalChooser.js');
    return { default: ComputerUpgradeModalChooser };
}, {});
const ComputerEducationModal = Dynamic(async () => {
    const { ComputerEducationModal } = await import('#pplx/frontend/apps/ask/onboarding/components/education-modals/ComputerEducationModal.js');
    return { default: ComputerEducationModal };
}, {});
export function useComputerBeforeSubmit({ enabled = true, isLocalMode = false, } = {}) {
    const canUseComputer = useCanUseComputer({ enabled });
    const viewerRestriction = useViewerComputerRestriction({ enabled });
    const refuseViewerSubmission = useRefuseViewerSubmission(viewerRestriction);
    const { data: accessDecision } = useAsiAccessDecision({ enabled });
    const isFreeTurnBudgetExhausted = useIsFreeTurnBudgetExhausted({ enabled });
    const isFreeTurnPreview = useIsFreeTurnPreview({ enabled });
    const { isEnterprise } = useIsSubscribed();
    const canStartTask = canUseComputer || (isLocalMode && allowsLocalComputerTask(accessDecision));
    const { openModal } = useModalContext();
    const { session, status } = useSession();
    const isAuthenticated = status === 'authenticated';
    const { track } = useInstrumentation();
    const openPaywallUpsell = usePaywallUpsell();
    const { openToast } = useToastContext();
    const { $t } = useIntl();
    const { data: gateData, isError: isGateError } = useQuery({
        queryKey: [...getComputerSubmitGateQueryKey(), session?.user?.id],
        queryFn: async () => {
            try {
                return await getComputerSubmitGate({ reason: 'computer-before-submit' });
            }
            catch (error) {
                // Cache an unavailable advisory check for one minute so mounts/focus do
                // not immediately reissue rate-limited requests. The backend still gates usage.
                if (error instanceof ApiClientsError && error.status === 429)
                    return null;
                throw error;
            }
        },
        staleTime: 1000 * 60,
        refetchOnWindowFocus: true,
        retry: false,
        // Cached credit data must not turn a failed refresh into a successful denial.
        meta: { skipPersistence: true },
        enabled: enabled && isAuthenticated,
    });
    const { variation: ubbConfig } = useAsiUbbConfigForConsumer();
    const credits = gateData?.balance_cents != null
        ? Math.round(gateData.balance_cents * ubbConfig.usd_cents_to_credits)
        : null;
    const isFreeUser = ['free', 'free_with_pm'].includes(session?.user?.payment_tier ?? '');
    const isComputerAdminDisabled = accessDecision?.denial_reason === 'admin_disabled';
    // `evaluated_for_user: false` on a denial means the access check itself failed
    // (e.g. the permissions platform was unreachable), not a verdict about the user.
    const isComputerAccessCheckUnavailable = accessDecision != null &&
        !accessDecision.can_use_computer &&
        accessDecision.evaluated_for_user === false;
    const trackUpgradeClick = useCallback(() => {
        track(computerLandingPageUpgradeClickedEvent({
            name: 'computer landing page upgrade clicked',
            data: { location: 'input_upsell' },
        }));
    }, [track]);
    const openConsumerUpgradeModal = useCallback(() => {
        trackUpgradeClick();
        openModal(ComputerUpgradeModalChooser, {}, { replace: true });
    }, [openModal, trackUpgradeClick]);
    const openEnterprisePaywall = useCallback(() => {
        trackUpgradeClick();
        openPaywallUpsell({
            origin: ModalOrigin.COMPUTER_PAGE,
            highlightTier: 'pro',
            defaultSegment: 'business',
            segments: ['business'],
        });
    }, [openPaywallUpsell, trackUpgradeClick]);
    return useCallback((options) => {
        track(computerQuerySubmitAttemptedEvent);
        if (!isAuthenticated) {
            trackUpgradeClick();
            openModal(ComputerEducationModal, { origin: ModalOrigin.COMPUTER_PAGE }, { replace: true });
            return true;
        }
        if (refuseViewerSubmission()) {
            return true;
        }
        if (isComputerAdminDisabled) {
            openToast({
                message: $t({
                    defaultMessage: 'Computer has been disabled by your admin',
                    id: '1dbe27f8fa',
                    description: 'Toast shown when an enterprise user without Computer access tries to submit a task',
                }),
                variant: 'neutral',
                timeoutSeconds: 5,
            });
            return true;
        }
        if (isEnterprise && isComputerAccessCheckUnavailable && !canStartTask) {
            openToast({
                message: $t({
                    defaultMessage: 'Computer is temporarily unavailable. Please try again in a moment.',
                    id: '0f7c5de7b3',
                    description: "Toast shown when an enterprise user's Computer access could not be checked because of a temporary outage",
                }),
                variant: 'neutral',
                timeoutSeconds: 5,
            });
            return true;
        }
        if (isEnterprise && !canStartTask) {
            openEnterprisePaywall();
            return true;
        }
        if (!canStartTask) {
            openConsumerUpgradeModal();
            return true;
        }
        // The backend checks the Ask allowance (Deep Research or asset task) before falling back to Computer credits/free turns.
        if (options?.isAskDeepResearch) {
            return false;
        }
        if (isFreeTurnBudgetExhausted) {
            openConsumerUpgradeModal();
            return true;
        }
        // Fail open on gate endpoint error — backend credits check handles gating
        if (isGateError) {
            return false;
        }
        if (!isLocalMode && gateData?.is_pro_at_spending_cap && credits !== null && credits <= 0) {
            trackUpgradeClick();
            openModal(ComputerUpgradeModalChooser, {
                tier: 'max',
                creditUpsellReturnPath: gateData.credit_upsell_return_to_thread_enabled
                    ? getCreditUpsellReturnPath(window.location.pathname)
                    : undefined,
            }, { replace: true });
            return true;
        }
        if (!isLocalMode &&
            isFreeUser &&
            !isFreeTurnPreview &&
            credits !== null &&
            credits <= 0 &&
            !gateData?.has_used_computer_before) {
            trackUpgradeClick();
            openModal(ComputerAddCreditsUpsellModal, {}, { replace: true });
            return true;
        }
        return false;
    }, [
        openModal,
        isAuthenticated,
        track,
        trackUpgradeClick,
        isEnterprise,
        isComputerAdminDisabled,
        isComputerAccessCheckUnavailable,
        refuseViewerSubmission,
        isLocalMode,
        openEnterprisePaywall,
        openConsumerUpgradeModal,
        openToast,
        $t,
        credits,
        gateData,
        canStartTask,
        isFreeTurnBudgetExhausted,
        isFreeTurnPreview,
        isFreeUser,
        isGateError,
    ]);
}
//# sourceMappingURL=useComputerBeforeSubmit.js.map